import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Save, Search, Tag } from 'lucide-react';
import type { VirtualUser, WardrobeItem } from '../../api/virtualUser';
import { getItemAttrs, updateItemAttrs, type ItemAttrs } from '../../api/itemAttrs';
import { presentRootCategories, rootCategoryLabel, categoryRoot } from '../../lib/wardrobeCategory';
import { useWardrobeFeed } from '../../hooks/useWardrobeFeed';
import { VirtualUserSelect } from '../shared/VirtualUserSelect';
import { WardrobeGrid } from '../compose/WardrobeGrid';
import { AttrField } from './AttrField';

interface Props {
  virtualUsers: VirtualUser[];
  selected: VirtualUser | null;
  onSelect: (u: VirtualUser) => void;
}

export function TaggingPage({ virtualUsers, selected, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [activeRoot, setActiveRoot] = useState('all');
  const [picked, setPicked] = useState<WardrobeItem | null>(null);
  const [detail, setDetail] = useState<ItemAttrs | null>(null);
  const [draftAttrs, setDraftAttrs] = useState<Record<string, string[]>>({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { items, loading, loadingMore, hasMore, counts, loadMore } = useWardrobeFeed(
    selected?.id,
    search,
    activeRoot,
    'category'
  );
  const roots = useMemo(() => presentRootCategories(Object.keys(counts)), [counts]);

  useEffect(() => {
    setPicked(null);
    setDetail(null);
    setMsg(null);
  }, [selected?.id]);

  useEffect(() => {
    if (!selected || !picked) return;
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      setMsg(null);
      try {
        const res = await getItemAttrs(selected.id, picked.cloth_id);
        if (cancelled) return;
        setDetail(res);
        setName(res.name);
        setDescription(res.description);
        const flat: Record<string, string[]> = {};
        for (const f of res.schema) {
          flat[f.key] = [...(res.attrs[f.key] ?? [])];
        }
        setDraftAttrs(flat);
      } catch (e) {
        if (!cancelled) setMsg(e instanceof Error ? e.message : '加载属性失败');
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected?.id, picked?.cloth_id]);

  const visibleFields = useMemo(() => {
    if (!detail) return [];
    const fam = detail.family;
    return detail.schema.filter((f) => f.family === 'common' || f.family === fam);
  }, [detail]);

  const save = async () => {
    if (!selected || !picked || !detail) return;
    setSaving(true);
    setMsg(null);
    try {
      const attrs: Record<string, string[]> = {};
      for (const f of visibleFields) {
        attrs[f.key] = (draftAttrs[f.key] ?? []).map((s) => s.trim()).filter(Boolean);
      }
      const res = await updateItemAttrs(selected.id, picked.cloth_id, { name, description, attrs });
      setDetail(res);
      setMsg('已写回业务库');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-12 border-b border-[var(--line)] bg-[var(--paper)] px-4 flex items-center gap-4 shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Tag className="w-4 h-4" />
          衣物打标
        </div>
        <VirtualUserSelect virtualUsers={virtualUsers} selected={selected} onSelect={onSelect} />
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            className="pl-8 pr-3 py-1.5 text-sm border border-[var(--line)] rounded-[var(--radius-sm)] bg-[var(--paper)] w-48"
            placeholder="搜索衣橱"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_360px]">
        <div className="min-h-0 flex flex-col border-r border-[var(--line)]">
          <div className="px-3 py-2 flex flex-wrap gap-1 border-b border-[var(--line)] bg-[var(--paper-2)]">
            <button
              className={`btn-ghost text-xs ${activeRoot === 'all' ? 'bg-[var(--accent-soft)]' : ''}`}
              onClick={() => setActiveRoot('all')}
            >
              全部
            </button>
            {roots.map((r) => (
              <button
                key={r}
                className={`btn-ghost text-xs ${activeRoot === r ? 'bg-[var(--accent-soft)]' : ''}`}
                onClick={() => setActiveRoot(r)}
              >
                {rootCategoryLabel(r)}
              </button>
            ))}
          </div>
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-3 scrollbar-thin">
            <WardrobeGrid
              items={items}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              onLoadMore={loadMore}
              usedIds={picked ? new Set([picked.cloth_id]) : new Set()}
              scrollRoot={scrollRef}
              pickMode
              onPickItem={(it) => setPicked(it)}
            />
          </div>
        </div>

        <aside className="min-h-0 overflow-y-auto p-4 bg-[var(--paper)] border-l border-[var(--line)] scrollbar-thin">
          {!picked && (
            <div className="text-sm text-[var(--muted)] pt-8 text-center">从左侧选择一件衣物 / 鞋 / 配饰开始打标</div>
          )}
          {picked && loadingDetail && !detail && (
            <div className="flex items-center justify-center gap-2 text-[var(--muted)] pt-10">
              <Loader2 className="w-4 h-4 animate-spin" />
              加载属性...
            </div>
          )}
          {picked && !loadingDetail && !detail && (
            <div className="pt-6 text-center">
              <div className="text-sm text-[var(--red)] mb-2">打标栏加载失败</div>
              <div className="text-xs text-[var(--muted)] break-all">{msg || '无法读取该单品属性'}</div>
            </div>
          )}
          {picked && detail && (
            <div className="space-y-4">
              <div className="flex gap-3">
                {detail.thumb ? (
                  <img src={detail.thumb} alt="" className="w-20 h-24 object-contain bg-[var(--paper-2)] rounded border border-[var(--line)]" />
                ) : (
                  <div className="w-20 h-24 bg-[var(--paper-2)] rounded border border-[var(--line)]" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[var(--muted)] mb-1">
                    {rootCategoryLabel(categoryRoot(detail.category))} · {detail.family} · {detail.id_type}
                  </div>
                  <input
                    className="w-full text-sm font-medium border border-[var(--line)] rounded px-2 py-1"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="名称"
                  />
                </div>
              </div>

              <label className="block text-xs text-[var(--muted)]">
                描述
                <textarea
                  className="mt-1 w-full text-sm border border-[var(--line)] rounded px-2 py-1.5 min-h-[64px]"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>

              <div className="space-y-3">
                {visibleFields.map((f) => (
                  <AttrField
                    key={f.key}
                    field={f}
                    values={draftAttrs[f.key] ?? []}
                    onChange={(values) => setDraftAttrs((prev) => ({ ...prev, [f.key]: values }))}
                  />
                ))}
              </div>

              <button className="btn-primary w-full" disabled={saving} onClick={save}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存并写回
              </button>
              {msg && <div className="text-xs text-[var(--muted)] text-center">{msg}</div>}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
